import type { MarkdownCountdownEditRequest } from './markdown-countdown';
import type { MarkdownEditorContextValue } from './markdown-editor-context';
import type { MarkdownImageEditRequest } from './markdown-image-node';
import type { MarkdownMapEditRequest } from './markdown-map';
import type { MarkdownMessageEditRequest } from './markdown-message';
import type { GalleryEditRequest } from './tools/gallery/editorjs-gallery-tool';
import type { JSONContent } from '@tiptap/core';
import type { EditorState } from '@tiptap/pm/state';

import { Box, Text } from '@chakra-ui/react';
import { Extension } from '@tiptap/core';
import Link from '@tiptap/extension-link';
import { TaskItem, TaskList } from '@tiptap/extension-list';
import { TableKit } from '@tiptap/extension-table';
import { Markdown } from '@tiptap/markdown';
import { Fragment, type Node as ProseMirrorNode } from '@tiptap/pm/model';
import { Plugin } from '@tiptap/pm/state';
import { TextSelection } from '@tiptap/pm/state';
import { EditorContent, useEditor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import './markdown-editor.scss';

import { toaster } from '#components/ui/toaster';
import {
  firstFileFromClipboardData,
  uploadPastedFile,
} from '#pages/document/upload-image';

import { MarkdownCountdownEditModal } from './markdown-countdown-edit-modal';
import { MarkdownCountdownNode } from './markdown-countdown-node';
import {
  createDefaultMarkdownDocumentValue,
  getDocumentTitleTagValidationMessage,
} from './markdown-document-title';
import { MarkdownDocumentTitleNode } from './markdown-document-title-node';
import { MarkdownFilesNode } from './markdown-files-node';
import { MarkdownGalleryNode } from './markdown-gallery-node';
import { MarkdownHighlightMenuPopover } from './markdown-highlight-menu-popover';
import { MarkdownImageEditModal } from './markdown-image-edit-modal';
import { MarkdownImageNode } from './markdown-image-node';
import { MarkdownMapEditModal } from './markdown-map-edit-modal';
import { MarkdownMapNode } from './markdown-map-node';
import { MarkdownMessageEditModal } from './markdown-message-edit-modal';
import { MarkdownMessageNode } from './markdown-message-node';
import { MarkdownSlashMenuExtension } from './markdown-slash-menu';
import { MarkdownSlashMenuPopover } from './markdown-slash-menu-popover';
import { MarkdownTableMenuPopover } from './markdown-table-menu-popover';

export type MarkdownEditorVariant = 'document' | 'grid-cell';

export interface MarkdownEditorProps {
  documentId?: string;
  apiHost: string;
  userEmail: string;
  value: string;
  onChange: (value: string) => void;
  parentPath: string;
  children: Array<{ id: string; path: string; label?: string }>;
  editorPlaceholderMuted?: boolean;
  isNewDocument: boolean;
  variant?: MarkdownEditorVariant;
  onCreateSubpage?: () => void;
  onNavigateToDocument?: (path: string) => void;
  onRequestRename?: () => void;
  onRequestGalleryEdit?: (request: GalleryEditRequest) => void;
  onUploaded?: () => void;
  /** When false, content is view-only (grid read-only mode). Default true. */
  editable?: boolean;
}

/** Props for MarkdownEditor inside a grid cell (value/onChange supplied per cell). */
export type GridCellMarkdownEditorProps = Omit<
  MarkdownEditorProps,
  'value' | 'onChange' | 'variant'
>;

function placeSelectionAfterDocumentTitle(
  editor: NonNullable<ReturnType<typeof useEditor>>,
) {
  const { doc } = editor.state;
  const titleNode = doc.firstChild;
  if (!titleNode || titleNode.type.name !== 'npDocumentTitle') {
    return;
  }

  const nextSelection = TextSelection.near(
    doc.resolve(Math.min(titleNode.nodeSize, doc.content.size)),
    1,
  );

  if (
    nextSelection.from === editor.state.selection.from &&
    nextSelection.to === editor.state.selection.to
  ) {
    return;
  }

  editor.view.dispatch(editor.state.tr.setSelection(nextSelection));
}

function pastedFileResultToContent(result: {
  kind: 'image' | 'link';
  href: string;
  text: string;
}): JSONContent {
  if (result.kind === 'image') {
    return {
      type: 'image',
      attrs: {
        src: result.href,
        alt: result.text,
        title: null,
        width: null,
        height: null,
      },
    };
  }

  return {
    type: 'text',
    text: result.text,
    marks: [
      {
        type: 'link',
        attrs: {
          href: result.href,
        },
      },
    ],
  };
}

function createTaskItemFromListItem(
  listItem: ProseMirrorNode,
  checked: boolean,
  remainingText: string,
  state: EditorState,
) {
  const { schema } = state;
  const paragraph = schema.nodes.paragraph.create(
    null,
    remainingText ? schema.text(remainingText) : undefined,
  );
  const content = [paragraph];
  for (let index = 1; index < listItem.childCount; index += 1) {
    content.push(listItem.child(index));
  }

  return schema.nodes.taskItem.create({ checked }, content);
}

function createTaskListInputNormalizationTransaction(state: EditorState) {
  const { schema } = state;
  const replacements: Array<{
    from: number;
    to: number;
    nodes: ProseMirrorNode[];
    selectionText: string | null;
  }> = [];

  state.doc.descendants((node, pos) => {
    if (node.type.name !== 'bulletList') {
      return true;
    }

    const groups: Array<{ type: 'bullet' | 'task'; nodes: ProseMirrorNode[] }> =
      [];
    let foundTaskMarker = false;
    let selectionText: string | null = null;
    let listItemStart = pos + 1;

    for (let index = 0; index < node.childCount; index += 1) {
      const listItem = node.child(index);
      const listItemEnd = listItemStart + listItem.nodeSize;
      const firstChild = listItem.firstChild;
      const match =
        firstChild?.type.name === 'paragraph'
          ? /^\[( |x|X)\]\s+(.+)$/.exec(firstChild.textContent)
          : null;
      const type = match ? 'task' : 'bullet';
      const previousGroup = groups[groups.length - 1];

      if (!previousGroup || previousGroup.type !== type) {
        groups.push({ type, nodes: [] });
      }

      const currentGroup = groups[groups.length - 1];

      if (match) {
        foundTaskMarker = true;
        const remainingText = match[2] ?? '';
        if (
          state.selection.from >= listItemStart &&
          state.selection.from <= listItemEnd
        ) {
          selectionText = remainingText;
        }

        currentGroup.nodes.push(
          createTaskItemFromListItem(
            listItem,
            match[1]?.toLowerCase() === 'x',
            remainingText,
            state,
          ),
        );
      } else {
        currentGroup.nodes.push(listItem);
      }

      listItemStart = listItemEnd;
    }

    if (!foundTaskMarker) {
      return true;
    }

    replacements.push({
      from: pos,
      to: pos + node.nodeSize,
      selectionText,
      nodes: groups.map((group) =>
        group.type === 'task'
          ? schema.nodes.taskList.create(null, group.nodes)
          : schema.nodes.bulletList.create(node.attrs, group.nodes),
      ),
    });

    return false;
  });

  if (replacements.length === 0) {
    return null;
  }

  const transaction = state.tr;
  let selectedText: string | null = null;
  for (let index = replacements.length - 1; index >= 0; index -= 1) {
    const replacement = replacements[index];
    if (replacement.selectionText !== null) {
      selectedText = replacement.selectionText;
      break;
    }
  }

  for (const replacement of replacements.reverse()) {
    transaction.replaceWith(
      replacement.from,
      replacement.to,
      Fragment.fromArray(replacement.nodes),
    );
  }

  if (selectedText !== null) {
    let selectionPosition: number | null = null;
    transaction.doc.descendants((node, pos) => {
      if (node.type.name !== 'paragraph' || node.textContent !== selectedText) {
        return true;
      }

      selectionPosition = pos + 1 + selectedText.length;
      return false;
    });

    if (selectionPosition !== null) {
      transaction.setSelection(
        TextSelection.near(transaction.doc.resolve(selectionPosition)),
      );
    }
  }

  return transaction;
}

const TaskListInputNormalizer = Extension.create({
  name: 'taskListInputNormalizer',

  addProseMirrorPlugins() {
    return [
      new Plugin({
        appendTransaction(transactions, _oldState, newState) {
          if (!transactions.some((transaction) => transaction.docChanged)) {
            return null;
          }

          return createTaskListInputNormalizationTransaction(newState);
        },
      }),
    ];
  },
});

export function MarkdownEditor({
  documentId,
  apiHost,
  userEmail,
  value,
  onChange,
  parentPath,
  children,
  editorPlaceholderMuted = false,
  isNewDocument,
  variant = 'document',
  onCreateSubpage,
  onNavigateToDocument,
  onRequestRename,
  onRequestGalleryEdit,
  onUploaded,
  editable = true,
}: MarkdownEditorProps) {
  const isGridCell = variant === 'grid-cell';
  const editorRef = useRef<ReturnType<typeof useEditor>>(null);
  const contextRef = useRef<MarkdownEditorContextValue>({
    documentId,
    apiHost,
    userEmail,
    parentPath,
    childDocuments: children,
    editorPlaceholderMuted,
    isNewDocument,
    onCreateSubpage,
    onNavigateToDocument,
    onRequestRename,
    onRequestGalleryEdit,
  });
  const [imageEditRequest, setImageEditRequest] =
    useState<MarkdownImageEditRequest | null>(null);
  const [countdownEditRequest, setCountdownEditRequest] =
    useState<MarkdownCountdownEditRequest | null>(null);
  const [mapEditRequest, setMapEditRequest] =
    useState<MarkdownMapEditRequest | null>(null);
  const [messageEditRequest, setMessageEditRequest] =
    useState<MarkdownMessageEditRequest | null>(null);
  const handleRequestImageEdit = useCallback(
    (request: MarkdownImageEditRequest) => {
      setImageEditRequest(request);
    },
    [],
  );
  const handleRequestCountdownEdit = useCallback(
    (request: MarkdownCountdownEditRequest) => {
      setCountdownEditRequest(request);
    },
    [],
  );
  const handleRequestMapEdit = useCallback(
    (request: MarkdownMapEditRequest) => {
      setMapEditRequest(request);
    },
    [],
  );
  const handleRequestMessageEdit = useCallback(
    (request: MarkdownMessageEditRequest) => {
      setMessageEditRequest(request);
    },
    [],
  );
  contextRef.current = {
    documentId,
    apiHost,
    userEmail,
    parentPath,
    childDocuments: children,
    editorPlaceholderMuted,
    isNewDocument,
    onCreateSubpage,
    onNavigateToDocument,
    onRequestRename,
    onRequestGalleryEdit,
    onRequestImageEdit: handleRequestImageEdit,
    onRequestCountdownEdit: handleRequestCountdownEdit,
    onRequestMapEdit: handleRequestMapEdit,
    onRequestMessageEdit: handleRequestMessageEdit,
  };

  const [validationMessage, setValidationMessage] = useState<string | null>(
    () => (isGridCell ? null : getDocumentTitleTagValidationMessage(value)),
  );
  const initialContent = isGridCell
    ? value
    : value.length > 0
      ? value
      : isNewDocument
        ? createDefaultMarkdownDocumentValue()
        : '';

  const handlePasteUpload = useCallback(
    async (event: ClipboardEvent, selection: { from: number; to: number }) => {
      const file = firstFileFromClipboardData(event.clipboardData);
      if (!file) {
        return false;
      }

      event.preventDefault();

      let result;
      try {
        result = await uploadPastedFile(file, {
          apiHost,
          userEmail,
          ...(documentId ? { documentId } : {}),
        });
      } catch (error) {
        toaster.create({
          title: 'Upload failed',
          description:
            error instanceof Error ? error.message : 'Could not upload file',
          type: 'error',
        });
        return true;
      }

      try {
        editorRef.current
          ?.chain()
          .focus()
          .insertContentAt(selection, pastedFileResultToContent(result))
          .run();
        onUploaded?.();
      } catch (error) {
        toaster.create({
          title: 'Paste failed',
          description:
            error instanceof Error
              ? error.message
              : 'Could not insert pasted file',
          type: 'error',
        });
      }

      return true;
    },
    [apiHost, documentId, onUploaded, userEmail],
  );

  const extensions = useMemo(
    () => [
      StarterKit,
      Link.configure({
        openOnClick: true,
        autolink: true,
        linkOnPaste: true,
      }),
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      TaskListInputNormalizer,
      TableKit.configure({
        table: {
          resizable: false,
          cellMinWidth: 80,
          HTMLAttributes: {
            class: 'markdown-table',
          },
        },
      }),
      Markdown.configure({
        markedOptions: { gfm: true },
      }),
      MarkdownDocumentTitleNode.configure({ contextRef }),
      MarkdownImageNode.configure({ contextRef }),
      MarkdownCountdownNode.configure({ contextRef }),
      MarkdownFilesNode.configure({ contextRef }),
      MarkdownGalleryNode.configure({ contextRef }),
      MarkdownMapNode.configure({ contextRef }),
      MarkdownMessageNode.configure({ contextRef }),
      MarkdownSlashMenuExtension.configure({
        onRequestImageEdit: handleRequestImageEdit,
        onRequestCountdownEdit: handleRequestCountdownEdit,
        onRequestMapEdit: handleRequestMapEdit,
        onRequestMessageEdit: handleRequestMessageEdit,
      }),
    ],
    [
      handleRequestCountdownEdit,
      handleRequestImageEdit,
      handleRequestMapEdit,
      handleRequestMessageEdit,
    ],
  );

  const editor = useEditor({
    extensions,
    content: initialContent,
    contentType: 'markdown',
    editable,
    immediatelyRender: false,
    editorProps: {
      attributes: {
        class: 'markdown-editor-content',
      },
      handleDOMEvents: {
        paste: (view, event) => {
          if (!(event instanceof ClipboardEvent)) {
            return false;
          }

          const file = firstFileFromClipboardData(event.clipboardData);
          if (!file) {
            return false;
          }

          void handlePasteUpload(event, {
            from: view.state.selection.from,
            to: view.state.selection.to,
          });
          return true;
        },
      },
    },
    onCreate: ({ editor }) => {
      editorRef.current = editor;
      if (!isGridCell) {
        setValidationMessage(
          getDocumentTitleTagValidationMessage(editor.getMarkdown()),
        );
        if (isNewDocument && value.length === 0) {
          const seeded = createDefaultMarkdownDocumentValue();
          editor.commands.setContent(seeded, {
            contentType: 'markdown',
            emitUpdate: false,
          });
        }

        placeSelectionAfterDocumentTitle(editor);
      }
    },
    onUpdate: ({ editor }) => {
      editorRef.current = editor;
      const next = editor.getMarkdown();
      onChange(next);
      if (!isGridCell) {
        setValidationMessage(getDocumentTitleTagValidationMessage(next));
      }
    },
  });

  useEffect(() => {
    if (!editor) return;

    const nextValue = isGridCell
      ? value
      : value.length > 0
        ? value
        : isNewDocument
          ? createDefaultMarkdownDocumentValue()
          : '';
    const currentValue = editor.getMarkdown();
    if (currentValue === nextValue) {
      if (!isGridCell) {
        setValidationMessage(
          getDocumentTitleTagValidationMessage(currentValue),
        );
      }

      return;
    }

    editor.commands.setContent(nextValue, {
      contentType: 'markdown',
      emitUpdate: false,
    });
    if (!isGridCell) {
      placeSelectionAfterDocumentTitle(editor);
      setValidationMessage(getDocumentTitleTagValidationMessage(nextValue));
    }
  }, [editor, isGridCell, isNewDocument, value]);

  useEffect(() => {
    if (!editor) return;

    editor.setEditable(editable);
  }, [editor, editable]);

  return (
    <div
      className={
        isGridCell
          ? 'markdown-editor-root markdown-editor-root--grid-cell'
          : 'markdown-editor-root'
      }
    >
      <MarkdownImageEditModal
        request={imageEditRequest}
        onDismiss={() => setImageEditRequest(null)}
      />
      <MarkdownCountdownEditModal
        request={countdownEditRequest}
        onDismiss={() => setCountdownEditRequest(null)}
      />
      <MarkdownMapEditModal
        request={mapEditRequest}
        onDismiss={() => setMapEditRequest(null)}
      />
      <MarkdownMessageEditModal
        request={messageEditRequest}
        onDismiss={() => setMessageEditRequest(null)}
      />
      {!isGridCell && validationMessage ? (
        <Box
          px={4}
          py={3}
          bg="orange.50"
          borderBottomWidth="1px"
          borderColor="orange.200"
        >
          <Text fontSize="sm" color="orange.700">
            {validationMessage}
          </Text>
        </Box>
      ) : null}

      <div className="markdown-editor-surface">
        <MarkdownHighlightMenuPopover editor={editor} />
        <MarkdownTableMenuPopover editor={editor} />
        <MarkdownSlashMenuPopover
          editor={editor}
          onRequestCountdownEdit={handleRequestCountdownEdit}
          onRequestImageEdit={handleRequestImageEdit}
          onRequestMapEdit={handleRequestMapEdit}
          onRequestMessageEdit={handleRequestMessageEdit}
        />
        <EditorContent editor={editor} />
      </div>
    </div>
  );
}
